/**
 * A browser call with a hosted agent: the client, the audio, the transcript and the clock.
 *
 * Try it on Settings uses it (`components/agent/useTestCall.ts`); `start` is how the session
 * is got, from the Worker, which starts the hosted agent on Pipecat Cloud and hands back the
 * room to join, so no Pipecat key is ever in a browser. The public demo page is demo-line's
 * page ported (`call/page.ts`) and drives its own handset, with the same two rules.
 *
 * THE FIRST TWO LINES OF `call()` ARE LOAD-BEARING. See `lib/call.ts`: the audio is unlocked
 * before the first await, or a phone plays nothing. `TryItDrawer.test.tsx` pins it.
 */

import { useEffect, useRef, useState } from "react";
import type { PipecatClient } from "@pipecat-ai/client-js";
import { ApiError } from "../api";
import { buildClient, openAudioContext, prefetchClient } from "./call";

export type Phase = "ready" | "starting" | "live" | "ended";

export interface Line {
  who: "agent" | "caller";
  text: string;
  /** A caller's line is replaced by each interim transcript until the final one arrives. */
  final?: boolean;
}

/** What the Worker answers with: Pipecat Cloud's room, and the call id it chose. */
export interface CallSession {
  callId: string;
  dailyRoom: string;
  dailyToken: string | null;
}

export function useCall(
  start: () => Promise<CallSession>,
  options: {
    /** After the call is over, with its id when it got one. */
    onEnded?: (callId: string | null) => void;
    /** Said when starting fails for a reason that is not the Worker's own sentence. */
    failure: string;
  },
) {
  const [phase, setPhase] = useState<Phase>("ready");
  const [lines, setLines] = useState<Line[]>([]);
  const [speaking, setSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);

  /** The one element the agent's voice plays through. The screen renders it; never unmounted mid-call. */
  const audio = useRef<HTMLAudioElement>(null);
  const context = useRef<AudioContext | null>(null);
  const client = useRef<PipecatClient | null>(null);
  const callId = useRef<string | null>(null);
  const startedAt = useRef<number | null>(null);
  const ended = useRef(false);
  // The latest of each, so a call started before a re-render still reaches the current ones.
  const latest = useRef({ start, options });
  latest.current = { start, options };

  // Loaded when the screen opens, so the import inside the click is already resolved and is
  // not the await that loses the tap. See `lib/call.ts`.
  useEffect(() => {
    void prefetchClient();
  }, []);

  useEffect(() => {
    if (phase !== "live") return;
    const timer = setInterval(() => {
      if (startedAt.current) setSeconds(Math.floor((Date.now() - startedAt.current) / 1000));
    }, 500);
    return () => clearInterval(timer);
  }, [phase]);

  // Leaving the screen hangs up. A call that outlives the only screen that can end it is a
  // call nobody can end.
  useEffect(() => () => void teardown(), []);

  function teardown() {
    const current = client.current;
    client.current = null;
    if (current) current.disconnect().catch(() => {});
    context.current?.close().catch(() => {});
    context.current = null;
    if (audio.current) audio.current.srcObject = null;
  }

  function end(reason: string | null) {
    if (ended.current) return;
    ended.current = true;
    teardown();
    setSpeaking(false);
    setPhase("ended");
    if (reason) setError(reason);
    latest.current.options.onEnded?.(callId.current);
  }

  async function call() {
    // BEFORE THE FIRST AWAIT, while the tap still counts. The play() has no source yet; what
    // it buys is the element's leave to play the agent's voice when it arrives.
    context.current ??= openAudioContext();
    audio.current?.play().catch(() => {});

    ended.current = false;
    callId.current = null;
    setError(null);
    setLines([]);
    setSeconds(0);
    setPhase("starting");

    try {
      const session = await latest.current.start();
      callId.current = session.callId;
      const modules = await prefetchClient();
      const next = buildClient(modules, {
        onConnected: () => {
          if (startedAt.current === null) startedAt.current = Date.now();
          setPhase((current) => (current === "starting" ? "live" : current));
        },
        onAgentSpeaking: (on) => {
          setSpeaking(on);
          // A new turn starts a new line; the words stream into it as they are spoken.
          if (on) setLines((all) => [...all, { who: "agent", text: "" }]);
        },
        onAgentText: (text) =>
          setLines((all) => {
            const last = all[all.length - 1];
            if (!last || last.who !== "agent") return [...all, { who: "agent", text }];
            return [...all.slice(0, -1), { who: "agent", text: last.text ? `${last.text} ${text}` : text }];
          }),
        onCallerText: (text, final) =>
          setLines((all) => {
            const last = all[all.length - 1];
            const line: Line = { who: "caller", text, final };
            return last?.who === "caller" && !last.final ? [...all.slice(0, -1), line] : [...all, line];
          }),
        onBotTrack: (track) => {
          // RULE TWO: through the element, never through the audio context.
          if (!audio.current) return;
          audio.current.srcObject = new MediaStream([track]);
          audio.current.play().catch(() => {});
        },
        onEnded: (reason) => end(reason === "microphone" ? "This needs your microphone. Allow access and try again." : null),
      });
      client.current = next;
      startedAt.current = null;
      await next.connect(session);
    } catch (err) {
      end(err instanceof ApiError ? err.message : latest.current.options.failure);
    }
  }

  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

  return { phase, lines, speaking, error, clock, audio, call, end };
}
